import { StoryCardPostContentBlockFields } from '../../../../../../services';
import {
  BlockTypes,
  HeaderBlock,
  ImageBlock,
  ListBlock,
  ListBlockElement,
  ListBlockStyle,
  ParagraphBlock,
  ParagraphBlockData,
  QuoteBlock,
  TableBlock,
} from '../../../../../models';
import { urlToFile } from '../../../../../utils';
import { DOCX_STYLE_MAP } from './docxStyleMap';
import { htmlToBlockText } from './htmlToBlockText';

import DOMPurify from 'dompurify';

const SUPPORTED_IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const DOCX_IMAGE_SRC_PREFIX = 'docx-image-';

const IMAGE_BLOCK_UPLOAD_LIMIT = 10;

const ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'blockquote',
  'img',
  'br',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'sub',
  'sup',
  'a',
  'code',
  'mark',
];

const ALLOWED_ATTR = ['href', 'alt', 'colspan', 'rowspan', 'src'];

export interface DocxParseResult {
  contentBlocks: StoryCardPostContentBlockFields[];
  imageFiles: File[];
  imageBlockIndexes: number[];
  skippedMediaCount: number;
}

interface ElementToBlockArgs {
  element: Element;
  index: number;
  currentBlockIndex: number;
  imageBlockIndexes: number[];
}

const headerToBlock = ({ element, index }: ElementToBlockArgs): HeaderBlock => ({
  id: `block-${index + 1}`,
  type: BlockTypes.header,
  data: { level: Number(element.tagName.slice(1)), text: htmlToBlockText(element) },
});

const paragraphToBlock = ({ element, index }: ElementToBlockArgs): ParagraphBlock => ({
  id: `block-${index + 1}`,
  type: BlockTypes.paragraph,
  data: { text: htmlToBlockText(element) },
});

const quoteToBlock = ({ element, index }: ElementToBlockArgs): QuoteBlock => ({
  id: `block-${index + 1}`,
  type: BlockTypes.quote,
  data: { text: htmlToBlockText(element), caption: '', alignment: 'left' },
});

const listItemContent = (item: Element): Element => {
  const contentElement = item.cloneNode(true) as Element;

  contentElement.querySelector(':scope > ul, :scope > ol')?.remove();

  return contentElement;
};

const hasText = (element: Element): boolean => Boolean(element.textContent?.trim());

const listItemToElements = (item: Element): ListBlockElement[] => {
  const nestedList = item.querySelector(':scope > ul, :scope > ol');

  const items = nestedList ? Array.from(nestedList.children).flatMap(listItemToElements) : [];

  const contentElement = listItemContent(item);

  if (!hasText(contentElement)) {
    return items;
  }

  return [{ content: htmlToBlockText(contentElement), items }];
};

const listToBlock = ({ element, index }: ElementToBlockArgs): ListBlock | undefined => {
  const firstItemWithText = Array.from(element.querySelectorAll('li')).find((item) =>
    hasText(listItemContent(item))
  );

  if (!firstItemWithText) {
    return undefined;
  }

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.list,
    data: {
      style:
        firstItemWithText.parentElement?.tagName.toLowerCase() === 'ol'
          ? ListBlockStyle.ordered
          : ListBlockStyle.unordered,
      items: Array.from(element.children).flatMap(listItemToElements),
    },
  };
};

const tableCellText = (cell: Element): string =>
  Array.from(cell.querySelectorAll(':scope > p'))
    .map((paragraph) => htmlToBlockText(paragraph))
    .join('\n');

const tableToBlock = ({ element, index }: ElementToBlockArgs): TableBlock => {
  const rows = Array.from(
    element.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tr')
  );

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.table,
    data: {
      withHeadings: Boolean(rows[0]?.querySelector('th')),
      content: rows.map((row) => Array.from(row.children).map(tableCellText)),
      columnWidths: Array.from(rows[0]?.children ?? [], () => null),
    },
  };
};

const imageParagraphToBlock = ({
  element,
  index,
  currentBlockIndex,
  imageBlockIndexes,
}: ElementToBlockArgs): ImageBlock | undefined => {
  const imageSourceIndexes = Array.from(element.querySelectorAll('img'))
    .map((image) => image.getAttribute('src') ?? '')
    .filter((src) => src.startsWith(DOCX_IMAGE_SRC_PREFIX))
    .map((src) => Number(src.slice(DOCX_IMAGE_SRC_PREFIX.length)))
    .slice(0, IMAGE_BLOCK_UPLOAD_LIMIT);

  if (!imageSourceIndexes.length) {
    return undefined;
  }

  imageSourceIndexes.forEach((sourceIndex) => {
    imageBlockIndexes[sourceIndex] = currentBlockIndex;
  });

  return {
    id: `block-${index + 1}`,
    type: BlockTypes.image,
    data: { items: [], withBorder: false, withBackground: false, stretched: false },
  };
};

const elementToBlocks = (args: ElementToBlockArgs): StoryCardPostContentBlockFields[] => {
  const { element, index } = args;

  const tagName = element.tagName.toLowerCase();

  if (/^h[1-6]$/.test(tagName)) {
    return [headerToBlock(args)];
  }

  if (tagName === 'blockquote') {
    return [quoteToBlock(args)];
  }

  if (tagName === 'ul' || tagName === 'ol') {
    const listBlock = listToBlock(args);

    return listBlock ? [listBlock] : [];
  }

  if (tagName === 'table') {
    return [tableToBlock(args)];
  }

  if (tagName === 'p') {
    const imageBlock = imageParagraphToBlock(args);

    const paragraphBlock = paragraphToBlock({
      ...args,
      index: imageBlock ? index + 1 : index,
    });

    const blocks = imageBlock ? [imageBlock] : [];

    return (paragraphBlock.data as ParagraphBlockData).text.trim()
      ? [...blocks, paragraphBlock]
      : blocks;
  }

  const text = element.textContent?.trim();

  return text ? [{ id: `block-${index + 1}`, type: BlockTypes.paragraph, data: { text } }] : [];
};

export const docxToContentBlocks = async (file: File): Promise<DocxParseResult> => {
  const arrayBuffer = await file.arrayBuffer();

  const mammoth = await import('mammoth');

  const imageFiles: File[] = [];

  const imageBlockIndexes: number[] = [];

  let skippedMediaCount = 0;

  const { value } = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      styleMap: DOCX_STYLE_MAP,
      includeDefaultStyleMap: true,
      ignoreEmptyParagraphs: true,
      convertImage: mammoth.images.imgElement(async (image) => {
        if (!SUPPORTED_IMAGE_CONTENT_TYPES.includes(image.contentType)) {
          skippedMediaCount += 1;

          return { src: '' };
        }

        const base64 = await image.read('base64').catch(() => '');

        if (!base64) {
          skippedMediaCount += 1;

          return { src: '' };
        }

        imageFiles.push(await urlToFile(`data:${image.contentType};base64,${base64}`));

        return { src: `${DOCX_IMAGE_SRC_PREFIX}${imageFiles.length - 1}` };
      }),
    }
  );

  const sanitizedHtml = DOMPurify.sanitize(value, { ALLOWED_TAGS, ALLOWED_ATTR });

  const { body } = new DOMParser().parseFromString(sanitizedHtml, 'text/html');

  const contentBlocks: StoryCardPostContentBlockFields[] = [];

  Array.from(body.children).forEach((element, index) => {
    contentBlocks.push(
      ...elementToBlocks({
        element,
        index,
        currentBlockIndex: contentBlocks.length,
        imageBlockIndexes,
      })
    );
  });

  return { contentBlocks, imageFiles, imageBlockIndexes, skippedMediaCount };
};
